import { useMemo, useState, useEffect } from 'react';
import { getEChartDynamicLineChartOption } from '../../../utils';
import { buildDimensionIconLegendOption } from '../utils';

import type { IEChartDynamicOption, IYAxisOption } from '@/pages/intelligence/types/echart';
import type { IStoreRankDetailsVisualisSourceData } from '@/pages/intelligence/types/storeRank';

export default function useVisualis(option: {
  isAreaEChart: boolean;
  isYAxisInverse: boolean;
  isShowYAxisName?: boolean;
  yAxisName?: string;
  yAxisNamePadding?: number | number[];
  sourceData: IStoreRankDetailsVisualisSourceData[];
  sourceDataDimensions: string[];
  sourceDataDimensionNameFormatter?: (name: string) => string;
  tooltipHeaderLeftIcon?: string;
  tooltipHeaderLeftTitle?: string;
  dimensionIconFormatter?: (name: string) => string;
}) {
  const {
    isAreaEChart,
    isYAxisInverse,
    isShowYAxisName = false,
    yAxisName = '',
    yAxisNamePadding,
    tooltipHeaderLeftIcon,
    tooltipHeaderLeftTitle,
    sourceData,
    sourceDataDimensions,
    sourceDataDimensionNameFormatter,
    dimensionIconFormatter,
  } = option || {};

  const [dynamicLineOption, setDynamicLineOption] = useState<IEChartDynamicOption>({
    series: [],
    xAxis: {
      data: [],
    },
    dataset: {
      dimensions: [],
      source: [],
    },
  });

  const yAxisOption = useMemo(() => {
    const optionTemp: IYAxisOption = {
      inverse: isYAxisInverse,
      name: isShowYAxisName && yAxisName ? yAxisName : '',
      nameLocation: isYAxisInverse && isShowYAxisName ? 'start' : 'end',
      yAxisNamePadding,
    };
    return optionTemp;
  }, [isYAxisInverse, isShowYAxisName, yAxisName, yAxisNamePadding]);

  const legendOption = useMemo(
    () =>
      buildDimensionIconLegendOption({
        sourceDataDimensions,
        dimensionIconFormatter,
        sourceDataDimensionNameFormatter,
      }),
    [sourceDataDimensionNameFormatter, dimensionIconFormatter, sourceDataDimensions],
  );

  const chartBubbleOption = useMemo(
    () => ({
      headerLeftIcon: tooltipHeaderLeftIcon || '',
      headerLeftTitle: tooltipHeaderLeftTitle || '-',
    }),
    [tooltipHeaderLeftIcon, tooltipHeaderLeftTitle],
  );

  useEffect(() => {
    const dynamicOption = getEChartDynamicLineChartOption({
      isAreaEChart,
      sourceData,
      sourceDataDimensions,
    });
    setDynamicLineOption(dynamicOption);
  }, [isAreaEChart, sourceData, sourceDataDimensions]);

  return {
    yAxisOption,
    legendOption,
    chartBubbleOption,
    dynamicLineOption,
  };
}
